import { useState, useEffect, createContext, useContext, useCallback, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, useParams, useLocation, Link } from "react-router-dom";
import axios from "axios";
import PaymentCelebrationHost from "@/components/pro/PaymentCelebrationHost";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import YormLoader from "@/components/YormLoader";


// Product rule (Feb 2026): only show the red/error toast. Silence every
// non-error variant globally by neutering the corresponding methods on
// the singleton `toast` object — every caller across the app that
// imports `toast` from "sonner" or the local shim shares this same
// object, so the override applies everywhere at once.
["success", "info", "warning", "message", "loading"].forEach((k) => {
  if (typeof toast[k] === "function") {
    toast[k] = () => {};
  }
});
// Bare `toast("Hello")` also defaults to the neutral variant — kill it
// too so only `toast.error(...)` shows anything on screen.
const _toastFn = typeof toast === "function" ? toast : null;
if (_toastFn) {
  // eslint-disable-next-line no-unused-vars
  toast.custom = () => {};
}
import { LanguageProvider } from "@/i18n/LanguageContext";
import { PlatformSettingsProvider } from "@/contexts/PlatformSettingsContext";
import { 
  initializeFirebase, 
  requestNotificationPermission, 
  registerFCMToken, 
  unregisterFCMToken,
  onForegroundMessage 
} from "@/utils/firebase";
import ForcePasswordUpdate from "@/components/ForcePasswordUpdate";
import ForceProfilePhotoUpload from "@/components/ForceProfilePhotoUpload";
import SplashScreen from "@/components/SplashScreen";
import { clearBiometricCredentials } from "@/utils/biometrics";
import { installZoomGuard } from "@/utils/zoomGuard";
import { trackPageView } from "@/utils/pageTracker";
import ErrorBoundary from "@/components/ErrorBoundary";
import IncomingTripListener from "@/components/IncomingTripListener";
import AcceptedJobSheet from "@/components/AcceptedJobSheet";
import CustomerAcceptedSheet from "@/components/CustomerAcceptedSheet";
import CustomerArrivedSheet from "@/components/CustomerArrivedSheet";
import RideFinishedSheet from "@/components/RideFinishedSheet";
import PaymentPrompts from "@/components/PaymentPrompts";
import KeyboardDock from "@/components/KeyboardDock";

// Each page fades in softly, like moving between iOS screens.
const PageFade = ({ children }) => {
  const { pathname } = useLocation();
  return <div key={pathname} className="page-fade">{children}</div>;
};
import ProRequestAlert from "@/components/ProRequestAlert";
import ProBookingRequestSheet from "@/components/ProBookingRequestSheet";
import RideUpdateListener from "@/components/RideUpdateListener";
import VanReminderListener from "@/components/VanReminderListener";
import PushPermissionPrompt from "@/components/PushPermissionPrompt";
import LocationPermissionPrompt from "@/components/LocationPermissionPrompt";
import PaystackCheckoutModal from "@/components/PaystackCheckoutModal";

// Loading spinner for lazy-loaded components
const PageLoader = YormLoader;

// Lazy load pages - Only load when needed (code splitting)
// Public pages - loaded first as they're most common entry points
const LandingPage = lazy(() => import("@/pages/LandingPage"));
const DownloadApp = lazy(() => import("@/pages/DownloadApp"));
const AboutYorm = lazy(() => import("@/pages/AboutYorm"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const RegisterPage = lazy(() => import("@/pages/RegisterPage"));
const ForgotPassword = lazy(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazy(() => import("@/pages/ResetPassword"));
const ResubmitPage = lazy(() => import("@/pages/ResubmitPage"));
const PrivacyPolicy = lazy(() => import("@/pages/PrivacyPolicy"));
const TermsAndConditions = lazy(() => import("@/pages/TermsAndConditions"));
const FAQ = lazy(() => import("@/pages/FAQ"));
const AboutUs = lazy(() => import("@/pages/AboutUs"));
const Contact = lazy(() => import("@/pages/Contact"));

// Customer pages - loaded only when customer logs in
const CustomerDashboard = lazy(() => import("@/pages/customer/Dashboard"));
const SearchResults = lazy(() => import("@/pages/customer/SearchResults"));
const ArtisanProfile = lazy(() => import("@/pages/customer/ArtisanProfile"));
const BookProfessional = lazy(() => import("@/pages/customer/BookProfessional"));
const CustomerBookings = lazy(() => import("@/pages/customer/Bookings"));
const CustomerChat = lazy(() => import("@/pages/customer/Chat"));
const CustomerChats = lazy(() => import("@/pages/customer/Chats"));
const CustomerFavorites = lazy(() => import("@/pages/customer/Favorites"));
const CustomerTaggedPlaces = lazy(() => import("@/pages/customer/TaggedPlaces"));
const SharedTag = lazy(() => import("@/pages/SharedTag"));
const CustomerSettings = lazy(() => import("@/pages/customer/Settings"));
const CustomerSupport = lazy(() => import("@/pages/customer/Support"));
const CustomerNotifications = lazy(() => import("@/pages/customer/Notifications"));
const CustomerRideshare = lazy(() => import("@/pages/customer/Rideshare"));
const CustomerLaundry = lazy(() => import("@/pages/customer/Laundry"));
const LaundryReturn = lazy(() => import("@/pages/customer/LaundryReturn"));
const CustomerTrash = lazy(() => import("@/pages/customer/Trash"));
const ArtisanTrashOnboarding = lazy(() => import("@/pages/artisan/TrashOnboarding"));
const AIRideResume = lazy(() => import("@/pages/customer/AIRideResume"));
const RideTrip = lazy(() => import("@/pages/RideTrip"));
const RideshareHistory = lazy(() => import("@/pages/RideshareHistory"));

// Artisan pages - loaded only when artisan logs in
const ArtisanDashboard = lazy(() => import("@/pages/artisan/Dashboard"));
const ArtisanProfileManage = lazy(() => import("@/pages/artisan/ProfileManage"));
const ArtisanBookings = lazy(() => import("@/pages/artisan/Bookings"));
const ArtisanInvoices = lazy(() => import("@/pages/artisan/Invoices"));
const ArtisanChat = lazy(() => import("@/pages/artisan/Chat"));
const ProRides = lazy(() => import("@/pages/artisan/ProRides"));
const ArtisanChats = lazy(() => import("@/pages/artisan/Chats"));
const ArtisanPromotions = lazy(() => import("@/pages/artisan/Promotions"));
const ArtisanPromotePicker = lazy(() => import("@/pages/artisan/PromotePicker"));
const ArtisanVehicles = lazy(() => import("@/pages/artisan/Vehicles"));
const ArtisanMyBusiness = lazy(() => import("@/pages/artisan/MyBusiness"));
const ArtisanLaundryOrders = lazy(() => import("@/pages/artisan/LaundryOrders"));
const ArtisanSchedule = lazy(() => import("@/pages/artisan/Schedule"));
const ArtisanReferral = lazy(() => import("@/pages/artisan/Referral"));
const ArtisanSupport = lazy(() => import("@/pages/artisan/Support"));
const ArtisanAnalytics = lazy(() => import("@/pages/artisan/Analytics"));
const ArtisanPayments = lazy(() => import("@/pages/artisan/Payments"));
const ArtisanNotifications = lazy(() => import("@/pages/artisan/Notifications"));
const SharedEarnings = lazy(() => import("@/pages/artisan/SharedEarnings"));
const ProPayoutLedger = lazy(() => import("@/pages/artisan/PayoutLedger"));
const ArtisanRideshareOptIn = lazy(() => import("@/pages/artisan/RideshareOptIn"));
const ArtisanRideshareOptInSingle = lazy(() => import("@/pages/artisan/RideshareOptInSingle"));
const ArtisanRidesharePriority = lazy(() => import("@/pages/artisan/RidesharePriority"));
const ArtisanRidesharePromotion = lazy(() => import("@/pages/artisan/RidesharePromotion"));
const ArtisanRideshareRate = lazy(() => import("@/pages/artisan/RideshareRate"));
const PaymentCallback = lazy(() => import("@/pages/artisan/PaymentCallback"));
const CustomerPaymentCallback = lazy(() => import("@/pages/CustomerPaymentCallback"));

// Admin pages - loaded only when admin logs in
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/Dashboard"));
const AdminArtisans = lazy(() => import("@/pages/admin/Artisans"));
const AdminReviews = lazy(() => import("@/pages/admin/AdminReviews"));
const AdminReports = lazy(() => import("@/pages/admin/Reports"));
const AdminAccounts = lazy(() => import("@/pages/admin/AdminAccounts"));
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminRevenue = lazy(() => import("@/pages/admin/Revenue"));
const AdminAccounting = lazy(() => import("@/pages/admin/Accounting"));
const HowItWorks = lazy(() => import("@/pages/HowItWorks"));
const HiwCapture = lazy(() => import("@/pages/HiwCapture"));
const AdminJobs = lazy(() => import("@/pages/admin/Jobs"));
const AdminPromotions = lazy(() => import("@/pages/admin/Promotions"));
const AdminSettings = lazy(() => import("@/pages/admin/Settings"));
const AdminLocations = lazy(() => import("@/pages/admin/Locations"));
const AdminUsers = lazy(() => import("@/pages/admin/Users"));
const AdminNotifications = lazy(() => import("@/pages/admin/Notifications"));
const AdminAnalytics = lazy(() => import("@/pages/admin/Analytics"));
const AdminAppAnalytics = lazy(() => import("@/pages/admin/AppAnalytics"));
const AdminBusinessClaims = lazy(() => import("@/pages/admin/BusinessClaims"));
const AdminAIHub = lazy(() => import("@/pages/admin/AIHub"));
const AdminLaundryDisputes = lazy(() => import("@/pages/admin/LaundryDisputes"));
const AdminPromoCredits = lazy(() => import("@/pages/admin/PromoCredits"));
const AdminSharedServices = lazy(() => import("@/pages/admin/SharedServices"));
const AdminLiveCommand = lazy(() => import("@/pages/admin/LiveCommand"));
const AdminMomoPayments = lazy(() => import("@/pages/admin/MomoPayments"));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Idle-timeout auto-logout.
//
// Product rule (Feb 2026): closing the app must NOT sign the user out.
// We only sign them out after a defined window of INACTIVITY across app
// launches. `yorm_last_activity` (epoch ms) is bumped on any real user
// interaction and every 5 minutes while the app is open. On cold-start
// we compare now vs that stamp — if the gap exceeds `IDLE_TIMEOUT_MS`
// we drop the token, otherwise we keep the cached session and let the
// background `/auth/me` verify.
const IDLE_TIMEOUT_MS = 7 * 24 * 60 * 60 * 1000; // 7 days of no activity
const LAST_ACTIVITY_KEY = "yorm_last_activity";

// Runs once at module load — BEFORE `AuthProvider` reads localStorage so
// the very first render sees the correct (possibly cleared) state and we
// don't flash a signed-in screen before booting the user out.
try {
  const stamp = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY) || "0", 10);
  if (stamp && Date.now() - stamp > IDLE_TIMEOUT_MS) {
    localStorage.removeItem("yorm_token");
    localStorage.removeItem("yorm_user");
    localStorage.removeItem(LAST_ACTIVITY_KEY);
  }
} catch (_) { /* localStorage disabled — nothing to do */ }

// Auth Context
export const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};

// Old "book as customer" mode was removed: put anyone still in it back on their pro account.
try {
  const saved = JSON.parse(localStorage.getItem("yorm_pro_session") || "null");
  if (saved?.token) { localStorage.setItem("yorm_token", saved.token); localStorage.setItem("yorm_user", JSON.stringify(saved.user)); }
  localStorage.removeItem("yorm_pro_session");
} catch (_) { /* ignore */ }

const AuthProvider = ({ children }) => {
  // Initialize state from localStorage
  const [user, setUser] = useState(() => {
    try {
      const storedUser = localStorage.getItem("yorm_user");
      return storedUser ? JSON.parse(storedUser) : null;
    } catch {
      return null;
    }
  });
  const [token, setToken] = useState(localStorage.getItem("yorm_token"));
  // OPTIMISTIC LOAD: If we already have a cached user + token, render the
  // app immediately with the cached identity while `/auth/me` verifies in
  // the background. Only show the blocking spinner on a genuine cold-start
  // (no cached user). Cuts perceived load time by 500-2000 ms on repeat
  // visits — the full-screen spinner used to gate every protected route
  // until `/auth/me` returned.
  const [loading, setLoading] = useState(() => {
    try {
      return !(localStorage.getItem("yorm_token") && localStorage.getItem("yorm_user"));
    } catch {
      return true;
    }
  });
  const [showPasswordUpdate, setShowPasswordUpdate] = useState(false);
  const [authError, setAuthError] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem("yorm_token");
      const storedUser = localStorage.getItem("yorm_user");
      
      if (storedToken) {
        try {
          const res = await axios.get(`${API}/auth/me?token=${storedToken}`);
          setUser(res.data);
          setToken(storedToken);
          setAuthError(null);
          // Store user data for offline access
          localStorage.setItem("yorm_user", JSON.stringify(res.data));
          // Check if password update is required
          if (res.data.password_update_required) {
            setShowPasswordUpdate(true);
          }
        } catch (e) {
          // Only clear token if it's an authentication error (401)
          if (e.response && e.response.status === 401) {
            console.log("Token invalid, clearing...");
            localStorage.removeItem("yorm_token");
            localStorage.removeItem("yorm_user");
            setToken(null);
            setUser(null);
            setAuthError(null);
          } else {
            // Network error or server error - use cached user data if available
            console.error("Auth check failed (network error):", e.message);
            if (storedUser) {
              try {
                const cachedUser = JSON.parse(storedUser);
                setUser(cachedUser);
                setToken(storedToken);
                setAuthError("Offline mode - using cached data");
              } catch {
                setAuthError("Connection error. Please check your internet.");
              }
            } else {
              setAuthError("Connection error. Please check your internet.");
            }
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const login = async (email, password, role) => {
    const res = await axios.post(`${API}/auth/login`, { email, password, role });
    const { access_token, user } = res.data;
    localStorage.setItem("yorm_token", access_token);
    localStorage.setItem("yorm_user", JSON.stringify(user));
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch (_) {}
    setToken(access_token);
    setUser(user);
    // Check if password update is required after login
    if (user.password_update_required) {
      setShowPasswordUpdate(true);
    }
    return user;
  };

  const requestOtp = async (phone, role) => {
    const res = await axios.post(`${API}/auth/otp/request`, { phone, role });
    return res.data;
  };

  const verifyOtp = async (phone, code, role) => {
    const res = await axios.post(`${API}/auth/otp/verify`, { phone, code, role });
    const { access_token, user } = res.data;
    localStorage.setItem("yorm_token", access_token);
    localStorage.setItem("yorm_user", JSON.stringify(user));
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch (_) {}
    setToken(access_token);
    setUser(user);
    if (user.password_update_required) {
      setShowPasswordUpdate(true);
    }
    return user;
  };

  const register = async (userData) => {
    const res = await axios.post(`${API}/auth/register`, userData);
    const { access_token, user } = res.data;
    // If account is pending approval, don't auto-login — the user must wait for admin
    if (user && user.approval_status === "pending") {
      return user;
    }
    localStorage.setItem("yorm_token", access_token);
    localStorage.setItem("yorm_user", JSON.stringify(user));
    try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch (_) {}
    setToken(access_token);
    setUser(user);
    return user;
  };

  const logout = async () => {
    try {
      // Unregister FCM token before logout
      const fcmToken = localStorage.getItem("yorm_fcm_token");
      if (fcmToken) {
        await unregisterFCMToken(fcmToken, token);
        localStorage.removeItem("yorm_fcm_token");
      }
      await axios.post(`${API}/auth/logout?token=${token}`);
    } catch (e) {
      // Ignore logout errors
    }
    // Wipe biometric-sealed credentials so the next person on this device
    // can't auto-unlock the previous user's account.
    try { await clearBiometricCredentials(); } catch (_) { /* best effort */ }
    localStorage.removeItem("yorm_token");
    localStorage.removeItem("yorm_user");
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setToken(null);
    setUser(null);
    setShowPasswordUpdate(false);
  };

  const handlePasswordUpdateSuccess = () => {
    setShowPasswordUpdate(false);
    // Update user state to reflect password_update_required = false
    if (user) {
      setUser({ ...user, password_update_required: false });
    }
  };

  const handlePasswordUpdateLogout = () => {
    logout();
    window.location.href = '/login';
  };

  // Heartbeat for activity tracking
  useEffect(() => {
    if (!token || !user) return;

    // Idle-timeout heartbeat: bump `yorm_last_activity` on any real user
    // interaction and every 5 minutes while the tab / native app is
    // open. Cold-start uses this stamp (see the module-level check at
    // the top of App.js) to decide whether the cached session is still
    // valid or the user has been idle past IDLE_TIMEOUT_MS.
    const bumpActivity = () => {
      try { localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now())); } catch (_) {}
    };
    bumpActivity();
    const activityEvents = ["click", "keydown", "touchstart", "scroll", "focus", "visibilitychange"];
    activityEvents.forEach((evt) => window.addEventListener(evt, bumpActivity, { passive: true }));
    const activityBump = setInterval(bumpActivity, 5 * 60 * 1000);

    // Live location tracking + activity heartbeat.
    //
    // Instead of polling `getCurrentPosition` every 60 seconds we subscribe
    // to the OS-level `watchPosition` stream — the device fires the callback
    // whenever the position actually changes (GPS, WiFi, cell). We keep the
    // freshest position in `latestPos.current` and push a heartbeat to the
    // server at most every 25 seconds so both sides stay live but we don't
    // hammer the network on every micro-movement.
    //
    // Live location is ALWAYS shared inside Yorm — there is no opt-out.
    // Read access is still gated server-side: only the counterparty on
    // a shared booking (or admin) can fetch a user's pin.
    //
    // RESILIENCE: a failed `getCurrentPosition` (permission not yet
    // granted, no GPS lock yet, transient OS error) must NEVER stop us
    // from retrying. The safety-net heartbeat below re-attempts GPS
    // acquisition on every tick so a pro who grants permission later,
    // or regains satellite lock, starts broadcasting live coords
    // without needing a page reload. Until the first live fix lands the
    // backend uses the registered signup city as the fallback pin.
    const latestPos = { current: null };
    const latestPosAt = { current: 0 };
    let lastSentAt = 0;

    // Lightweight one-shot GPS probe — resolves to a coord or null,
    // never rejects. We reuse it from both the kickoff path and the
    // safety-net tick so retries don't duplicate logic.
    const probeGps = () => new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      try {
        navigator.geolocation.getCurrentPosition(
          (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
          () => resolve(null),
          { enableHighAccuracy: true, timeout: 6000, maximumAge: 10000 }
        );
      } catch { resolve(null); }
    });

    const sendHeartbeat = async (posOverride) => {
      try {
        let pos = posOverride || latestPos.current;
        // Treat coords older than 60 s as stale — try a fresh probe so
        // a pro who just regained GPS stops being pinned to the
        // registered-city fallback on the shared services map.
        const stale = !pos || (Date.now() - latestPosAt.current) > 60000;
        if (stale) {
          const fresh = await probeGps();
          if (fresh) {
            latestPos.current = fresh;
            latestPosAt.current = Date.now();
            pos = fresh;
          }
        }
        let url = `${API}/activity/heartbeat?token=${token}`;
        if (pos && pos.lat != null && pos.lng != null) {
          url += `&lat=${pos.lat}&lng=${pos.lng}`;
        }
        await axios.post(url);
        lastSentAt = Date.now();
      } catch (e) {
        // Silently fail heartbeat errors — we retry on the next tick.
      }
    };

    // Kick off with a one-shot so the first heartbeat carries coords even
    // before the watchPosition subscription has fired its first update.
    (async () => {
      const first = await probeGps();
      if (first) {
        latestPos.current = first;
        latestPosAt.current = Date.now();
      }
      sendHeartbeat();
    })();

    // Live stream — every OS-level GPS/WiFi update lands here. We
    // throttle the actual network write to a snappy 8 s cadence so
    // customers watching the search-pro map see near-real-time driver
    // movement. `maximumAge:5000` asks the OS for fresher coords too.
    // On error (permission revoked, GPS lost) we don't bail — the
    // safety-net tick below will keep probing.
    let watchId = null;
    if (navigator.geolocation && navigator.geolocation.watchPosition) {
      try {
        watchId = navigator.geolocation.watchPosition(
          (pos) => {
            latestPos.current = { lat: pos.coords.latitude, lng: pos.coords.longitude };
            latestPosAt.current = Date.now();
            if (Date.now() - lastSentAt > 8000) sendHeartbeat(latestPos.current);
          },
          () => {
            // Mark the current pos as stale so the next safety-net tick
            // probes afresh — we never give up on live GPS.
            latestPosAt.current = 0;
          },
          { enableHighAccuracy: true, maximumAge: 5000, timeout: 30000 }
        );
      } catch { /* ignore */ }
    }

    // Safety-net timer — fires every 15 s to keep last_active fresh
    // and to retry GPS acquisition when watchPosition hasn't produced
    // a fix (user hasn't granted permission yet, OS throttling, etc.).
    const interval = setInterval(() => sendHeartbeat(), 15000);

    return () => {
      clearInterval(interval);
      clearInterval(activityBump);
      activityEvents.forEach((evt) => window.removeEventListener(evt, bumpActivity));
      if (watchId != null && navigator.geolocation && navigator.geolocation.clearWatch) {
        try { navigator.geolocation.clearWatch(watchId); } catch { /* ignore */ }
      }
    };
  }, [token, user]);

  // Firebase push notification initialization
  useEffect(() => {
    if (!token || !user) return;
    
    const initNotifications = async () => {
      try {
        // Initialize Firebase (now async — dynamically loads the SDK).
        await initializeFirebase();
        
        // Request permission and get FCM token
        const fcmToken = await requestNotificationPermission();
        
        if (fcmToken) {
          // Register with backend
          const registered = await registerFCMToken(fcmToken, token);
          if (registered) {
            localStorage.setItem("yorm_fcm_token", fcmToken);
            console.log("Push notifications enabled");
          }
        }
        
        // Listen for foreground messages
        const unsubscribe = onForegroundMessage((payload) => {
          console.log("Foreground message:", payload);
          
          const { notification, data } = payload;

          // Broadcast a global "booking status changed" event so any
          // open Bookings / RideTrip / Chat page can refetch immediately.
          // Fires on booking_accepted, booking_declined, new_booking,
          // rideshare_accepted, rideshare_declined, offer_accepted, and
          // any booking-shaped notification with a booking_id. Consumers
          // use `window.addEventListener('yorm:booking-updated', ...)`
          // and read `event.detail`.
          try {
            const bt = String(data?.type || '');
            if (bt.startsWith('document_')) window.dispatchEvent(new Event('yorm:messages-updated'));
            const isBookingEvent =
              bt.startsWith('booking_') ||
              bt.startsWith('rideshare_') ||
              bt.startsWith('offer_') ||
              bt.startsWith('trip_') ||
              bt.startsWith('ride_') ||
              bt.startsWith('laundry_') ||
              bt === 'new_booking' ||
              Boolean(data?.booking_id);
            if (isBookingEvent) {
              window.dispatchEvent(new CustomEvent('yorm:booking-updated', {
                detail: { type: bt, booking_id: data?.booking_id, raw: data },
              }));
            }
          } catch (_err) {
            // Non-fatal — the toast still renders below.
          }
          
          // Show toast for foreground notifications with click action
          if (notification) {
            const navigateToDestination = () => {
              if (String(data?.type || "").startsWith("document_") && data?.url) {
                window.location.href = data.url;
              } else if (data?.type === "boost_expiring" && data?.url) {
                window.location.href = data.url;
              } else if (data?.type === "new_message" && data?.booking_id) {
                // Navigate to chat based on recipient's role from notification data
                const chatPath = data.user_role === "artisan" 
                  ? `/artisan/chat/${data.booking_id}`
                  : `/customer/chat/${data.booking_id}`;
                window.location.href = chatPath;
              } else if (data?.type === "new_booking" && data?.booking_id) {
                window.location.href = `/artisan/chat/${data.booking_id}`;
              } else if (data?.type === "admin_notification") {
                const notifPath = user?.role === "artisan" 
                  ? "/artisan/notifications" 
                  : "/customer/notifications";
                window.location.href = notifPath;
              } else if (data?.booking_id) {
                const chatPath = user?.role === "artisan" 
                  ? `/artisan/chat/${data.booking_id}`
                  : `/customer/chat/${data.booking_id}`;
                window.location.href = chatPath;
              } else if (data?.type?.includes("booking_")) {
                window.location.href = "/customer/bookings";
              }
            };
            
            // Determine button label based on notification type
            let buttonLabel = "View";
            if (data?.type === "boost_expiring") {
              buttonLabel = "Extend";
            } else if (data?.type === "new_message") {
              buttonLabel = "Open Chat";
            } else if (data?.booking_id) {
              buttonLabel = "Open Chat";
            }
            
            // Make notification clickable to go to destination
            toast(notification.title, {
              description: notification.body,
              duration: 10000,
              action: {
                label: buttonLabel,
                onClick: navigateToDestination
              }
            });
          }
        });
        
        return unsubscribe;
      } catch (error) {
        console.error("Notification init error:", error);
      }
    };
    
    initNotifications();
  }, [token, user]);

  // Handle service worker notification click messages
  useEffect(() => {
    const handleServiceWorkerMessage = (event) => {
      console.log("Service worker message:", event.data);
      
      if (event.data?.type === 'NOTIFICATION_CLICK') {
        const { navigateTo, data, action } = event.data;
        
        // Navigate to the specified URL
        if (navigateTo) {
          window.location.href = navigateTo;
        }
      }
    };
    
    navigator.serviceWorker?.addEventListener('message', handleServiceWorkerMessage);
    
    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleServiceWorkerMessage);
    };
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, token, loading, login, requestOtp, verifyOtp, register, logout, authError }}>
      {showPasswordUpdate && user && (
        <ForcePasswordUpdate 
          onSuccess={handlePasswordUpdateSuccess} 
          onLogout={handlePasswordUpdateLogout} 
        />
      )}
      {/* Block legacy artisans (registered before the mandatory-photo check
          existed) from using the app until they upload a profile photo.
          The modal closes itself once the backend stops reporting
          needs_profile_photo on the next /auth/me round-trip. */}
      {!showPasswordUpdate && user?.role === "artisan" && user?.needs_profile_photo && (
        <ForceProfilePhotoUpload />
      )}
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
// Ride alerts link riders to /customer/trip/:id; a pro riding to a job opens it in the pro app instead.
const CustomerTripRoute = () => {
  const { id } = useParams();
  const { user } = useAuth();
  if (user?.role === "artisan") return <Navigate to={`/professional/rides/${id}`} replace />;
  return <ProtectedRoute allowedRoles={["customer"]}><RideTrip /></ProtectedRoute>;
};

const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return <YormLoader />;
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedRoles && !allowedRoles.includes(user.role)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Component to redirect artisans to dashboard on app open
const ArtisanDashboardRedirect = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // If user is a professional and they're on a public page (not already on a professional page),
  // redirect them to the dashboard
  useEffect(() => {
    if (!loading && user?.role === "artisan") {
      const isOnArtisanPage = location.pathname.startsWith("/artisan");
      const isOnPublicPage = ["/", "/home", "/download", "/app", "/login", "/register"].includes(location.pathname);
      
      if (isOnPublicPage || (!isOnArtisanPage && location.pathname !== "/")) {
        // Store that we should redirect to dashboard
        sessionStorage.setItem("yorm_artisan_redirect", "true");
      }
    }
  }, [user, loading, location.pathname]);
  
  return null;
};

// Anonymous page-view tracker — fires beacon on every route change.
const PageViewTracker = () => {
  const location = useLocation();
  const { user } = useAuth();
  useEffect(() => {
    const isNative = typeof window !== "undefined" && !!window.Capacitor?.isNativePlatform?.();
    trackPageView({
      api: API,
      path: location.pathname + (location.search || ""),
      userId: user?.id || null,
      role: user?.role || null,
      isNative,
    });
  }, [location.pathname, location.search, user?.id, user?.role]);
  return null;
};

// Wrapper to check and redirect artisans to dashboard
const ArtisanAutoRedirect = ({ children }) => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    if (!loading && user?.role === "artisan") {
      // If artisan is on root, home, or other public pages, redirect to dashboard
      const publicPages = ["/", "/home", "/download", "/app"];
      if (publicPages.includes(location.pathname)) {
        navigate("/artisan", { replace: true });
      }
    }
  }, [user, loading, location.pathname, navigate]);
  
  return children;
};

function App() {
  // Initialize splash state based on session storage
  const [showSplash, setShowSplash] = useState(() => {
    return !sessionStorage.getItem("yorm_splash_shown");
  });

  // Hard zoom guard for iOS Safari — Apple ignores `user-scalable=no` in the
  // viewport meta, so we have to intercept gesturestart / multi-touch events
  // and trackpad pinch wheel events directly. Lives in utils/zoomGuard.js.
  useEffect(() => {
    const cleanup = installZoomGuard();
    return cleanup;
  }, []);

  const handleSplashComplete = () => {
    sessionStorage.setItem("yorm_splash_shown", "true");
    setShowSplash(false);
  };

  return (
    <AuthProvider>
    <LanguageProvider>
      <PlatformSettingsProvider>
      <div className="App">
        {/* While the splash is up we return early — mounting nothing else
            means no header, footer, dialog portal, toast, or map overlay
            can bleed through it. Every subsequent screen still renders as
            usual once the splash finishes. */}
        {showSplash ? (
          <SplashScreen onComplete={handleSplashComplete} duration={2600} />
        ) : (
          <BrowserRouter>
          <PageViewTracker />
          <IncomingTripListener />
          <AcceptedJobSheet />
          <CustomerAcceptedSheet />
          <CustomerArrivedSheet />
          <RideFinishedSheet />
          <PaymentPrompts />
          <KeyboardDock />
          <ProRequestAlert />
          <ProBookingRequestSheet />
          <PaymentCelebrationHost />
          <RideUpdateListener />
          <VanReminderListener />
          <PushPermissionPrompt />
          <LocationPermissionPrompt />
          <PaystackCheckoutModal />
          <ArtisanAutoRedirect>
          <ErrorBoundary>
          <Suspense fallback={<PageLoader />}>
            <PageFade>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<DownloadApp />} />
              <Route path="/download" element={<DownloadApp />} />
              <Route path="/about-yorm" element={<AboutYorm />} />
              <Route path="/app" element={<DownloadApp />} />
              {/* Share-friendly slug — carries the "Connecting Skills to
                  Opportunities" phrase in the URL for shared links. */}
              <Route path="/connecting-skills-to-opportunities" element={<DownloadApp />} />
              <Route path="/home" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/how-it-works" element={<HowItWorks />} />
              <Route path="/hiw-capture" element={<HiwCapture />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/resubmit" element={<ResubmitPage />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/terms" element={<TermsAndConditions />} />
              <Route path="/faq" element={<FAQ />} />
              <Route path="/about" element={<AboutUs />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/admin/login" element={<AdminLogin />} />

              {/* Protected Routes - Require Login */}
              <Route path="/search" element={<ProtectedRoute allowedRoles={["customer", "admin"]}><SearchResults /></ProtectedRoute>} />
              <Route path="/artisan/:id" element={<ProtectedRoute allowedRoles={["customer", "admin"]}><ArtisanProfile /></ProtectedRoute>} />

              {/* Customer Routes */}
              <Route path="/customer" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerDashboard /></ProtectedRoute>} />
              <Route path="/customer/bookings" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerBookings /></ProtectedRoute>} />
              <Route path="/customer/book/:artisanId" element={<ProtectedRoute allowedRoles={["customer"]}><BookProfessional /></ProtectedRoute>} />
              <Route path="/customer/favorites" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerFavorites /></ProtectedRoute>} />
              <Route path="/customer/tagged-places" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerTaggedPlaces /></ProtectedRoute>} />
              <Route path="/t/:code" element={<SharedTag />} />
              <Route path="/customer/chats" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerChats /></ProtectedRoute>} />
              <Route path="/customer/chat/:bookingId" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerChat /></ProtectedRoute>} />
              <Route path="/customer/support" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerSupport /></ProtectedRoute>} />
              <Route path="/customer/how-it-works" element={<ProtectedRoute allowedRoles={["customer"]}><HowItWorks /></ProtectedRoute>} />
              <Route path="/customer/settings" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerSettings /></ProtectedRoute>} />
              <Route path="/customer/notifications" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerNotifications /></ProtectedRoute>} />
              <Route path="/customer/documents" element={<Navigate to="/customer/chats?tab=docs" replace />} />
              <Route path="/customer/rideshare/:type" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerRideshare /></ProtectedRoute>} />
              <Route path="/customer/laundry" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerLaundry /></ProtectedRoute>} />
              <Route path="/laundry/return/:orderId" element={<ProtectedRoute allowedRoles={["customer"]}><LaundryReturn /></ProtectedRoute>} />
              <Route path="/customer/trash" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerTrash /></ProtectedRoute>} />
              <Route path="/professional/rideshare/trash" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanTrashOnboarding /></ProtectedRoute>} />
              <Route path="/customer/ai-ride-resume" element={<ProtectedRoute allowedRoles={["customer"]}><AIRideResume /></ProtectedRoute>} />
              <Route path="/customer/trip/:id" element={<CustomerTripRoute />} />
              <Route path="/customer/rideshare-history" element={<ProtectedRoute allowedRoles={["customer"]}><RideshareHistory /></ProtectedRoute>} />
              <Route path="/customer/payment/callback" element={<ProtectedRoute allowedRoles={["customer"]}><CustomerPaymentCallback /></ProtectedRoute>} />

              {/* Artisan Routes (legacy `/artisan/*` slugs mirrored under
                  `/professional/*` because the UI was rebranded but
                  in-app navigation now points at `/professional/*`.
                  Both slugs render the same components so old links,
                  bookmarks, email links, and push-notification deep
                  links keep working). */}
              <Route path="/artisan" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanDashboard /></ProtectedRoute>} />
              <Route path="/artisan/profile" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanProfileManage /></ProtectedRoute>} />
              <Route path="/artisan/bookings" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanBookings /></ProtectedRoute>} />
              <Route path="/artisan/invoices" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanInvoices /></ProtectedRoute>} />
              <Route path="/artisan/documents" element={<Navigate to="/professional/invoices" replace />} />
              <Route path="/professional/documents" element={<Navigate to="/professional/invoices" replace />} />
              <Route path="/artisan/chats" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanChats /></ProtectedRoute>} />
              <Route path="/artisan/chat/:bookingId" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanChat /></ProtectedRoute>} />
              <Route path="/artisan/promotions" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanPromotions /></ProtectedRoute>} />
              <Route path="/artisan/vehicles" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanVehicles /></ProtectedRoute>} />
              <Route path="/professional/vehicles" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanVehicles /></ProtectedRoute>} />
              <Route path="/professional/business" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanMyBusiness /></ProtectedRoute>} />
              <Route path="/professional/business/:linkId/laundry" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanLaundryOrders /></ProtectedRoute>} />
              <Route path="/artisan/schedule" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanSchedule /></ProtectedRoute>} />
              <Route path="/professional/schedule" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanSchedule /></ProtectedRoute>} />
              <Route path="/artisan/referral" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanReferral /></ProtectedRoute>} />
              <Route path="/artisan/payment/callback" element={<ProtectedRoute allowedRoles={["artisan"]}><PaymentCallback /></ProtectedRoute>} />
              <Route path="/artisan/support" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanSupport /></ProtectedRoute>} />
              <Route path="/artisan/analytics" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanAnalytics /></ProtectedRoute>} />
              <Route path="/artisan/payments" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanPayments /></ProtectedRoute>} />
              <Route path="/artisan/notifications" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanNotifications /></ProtectedRoute>} />
              <Route path="/artisan/rideshare" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanRideshareOptIn /></ProtectedRoute>} />
              <Route path="/artisan/trip/:id" element={<ProtectedRoute allowedRoles={["artisan"]}><RideTrip /></ProtectedRoute>} />
              <Route path="/artisan/rideshare-history" element={<ProtectedRoute allowedRoles={["artisan"]}><RideshareHistory /></ProtectedRoute>} />

              {/* Professional Routes (the rebranded slug used by every
                  in-app link, header, footer and deep link). */}
              <Route path="/professional" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanDashboard /></ProtectedRoute>} />
              <Route path="/professional/profile" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanProfileManage /></ProtectedRoute>} />
              <Route path="/professional/bookings" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanBookings /></ProtectedRoute>} />
              <Route path="/professional/invoices" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanInvoices /></ProtectedRoute>} />
              <Route path="/professional/chats" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanChats /></ProtectedRoute>} />
              <Route path="/professional/chat/:bookingId" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanChat /></ProtectedRoute>} />
              <Route path="/professional/promotions" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanPromotions /></ProtectedRoute>} />
              <Route path="/professional/promote" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanPromotePicker /></ProtectedRoute>} />
              <Route path="/professional/referral" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanReferral /></ProtectedRoute>} />
              <Route path="/professional/payment/callback" element={<ProtectedRoute allowedRoles={["artisan"]}><PaymentCallback /></ProtectedRoute>} />
              <Route path="/professional/support" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanSupport /></ProtectedRoute>} />
              <Route path="/professional/how-it-works" element={<ProtectedRoute allowedRoles={["artisan"]}><HowItWorks /></ProtectedRoute>} />
              <Route path="/professional/analytics" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanAnalytics /></ProtectedRoute>} />
              <Route path="/professional/payments" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanPayments /></ProtectedRoute>} />
              <Route path="/professional/notifications" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanNotifications /></ProtectedRoute>} />
              <Route path="/professional/shared-earnings" element={<ProtectedRoute allowedRoles={["artisan"]}><SharedEarnings /></ProtectedRoute>} />
              <Route path="/professional/payout-ledger" element={<ProtectedRoute allowedRoles={["artisan"]}><ProPayoutLedger /></ProtectedRoute>} />
              <Route path="/professional/rideshare" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanRideshareOptIn /></ProtectedRoute>} />
              <Route path="/professional/rideshare/enroll/:service" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanRideshareOptInSingle /></ProtectedRoute>} />
              <Route path="/professional/rideshare/priority" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanRidesharePriority /></ProtectedRoute>} />
              <Route path="/professional/rideshare/promotion" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanRidesharePromotion /></ProtectedRoute>} />
              <Route path="/professional/rideshare/rate" element={<ProtectedRoute allowedRoles={["artisan"]}><ArtisanRideshareRate /></ProtectedRoute>} />
              <Route path="/professional/trip/:id" element={<ProtectedRoute allowedRoles={["artisan"]}><RideTrip /></ProtectedRoute>} />
              <Route path="/professional/rides" element={<ProtectedRoute allowedRoles={["artisan"]}><ProRides /></ProtectedRoute>} />
              <Route path="/professional/rides/:id" element={<ProtectedRoute allowedRoles={["artisan"]}><RideTrip asRider /></ProtectedRoute>} />
              <Route path="/professional/rideshare-history" element={<ProtectedRoute allowedRoles={["artisan"]}><RideshareHistory /></ProtectedRoute>} />

              {/* Admin Routes - Using Layout with Outlet */}
              <Route path="/admin" element={<ProtectedRoute allowedRoles={["admin"]}><AdminLayout /></ProtectedRoute>}>
                <Route index element={<AdminDashboard />} />
                <Route path="ai" element={<AdminAIHub />} />
                <Route path="yorm-ai" element={<Navigate to="/admin/ai?tab=assistant" replace />} />
                <Route path="slang" element={<Navigate to="/admin/ai?tab=slang" replace />} />
                <Route path="business-claims" element={<AdminBusinessClaims />} />
                <Route path="cache" element={<Navigate to="/admin/ai?tab=cache" replace />} />
                <Route path="laundry-disputes" element={<AdminLaundryDisputes />} />
                <Route path="analytics" element={<AdminAnalytics />} />
                <Route path="app-analytics" element={<AdminAppAnalytics />} />
                <Route path="artisans" element={<AdminArtisans />} />
                <Route path="professionals" element={<AdminArtisans />} />
                <Route path="reviews" element={<AdminReviews />} />
                <Route path="reports" element={<AdminReports />} />
                <Route path="admins" element={<AdminAccounts />} />
                <Route path="users" element={<AdminUsers />} />
                <Route path="jobs" element={<AdminJobs />} />
                <Route path="promotions" element={<AdminPromotions />} />
                <Route path="payments" element={<AdminRevenue />} />
                <Route path="revenue" element={<AdminRevenue />} />
                <Route path="accounting" element={<AdminAccounting />} />
                <Route path="how-it-works" element={<HowItWorks />} />
                <Route path="payouts" element={<Navigate to="/admin/momo-payments?tab=payouts" replace />} />
                <Route path="momo-payments" element={<AdminMomoPayments />} />
                <Route path="promo-credits" element={<AdminPromoCredits />} />
                <Route path="shared-services" element={<AdminSharedServices />} />
                {/* Legacy admin routes redirected into the merged Shared
                    Services surface so any old links / bookmarks land on
                    the same page. */}
                <Route path="rideshare" element={<AdminSharedServices />} />
                <Route path="vehicles" element={<AdminSharedServices />} />
                <Route path="live-command" element={<AdminLiveCommand />} />
                <Route path="locations" element={<AdminLocations />} />
                <Route path="notifications" element={<AdminNotifications />} />
                <Route path="settings" element={<AdminSettings />} />
                {/* Catch-all inside /admin: any unknown admin sub-route falls
                    back to the Dashboard instead of rendering a blank Outlet. */}
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Route>
            </Routes>
            </PageFade>
          </Suspense>
          </ErrorBoundary>
          </ArtisanAutoRedirect>
        </BrowserRouter>
        )}
        {!showSplash && (
          <Toaster
            position="bottom-center"
            richColors
            closeButton
            // Lift toasts above the bottom nav bar (~72 px) plus the iOS
            // home-indicator safe area so they never cover Search / Favorites
            // / Home / Messages / Bookings buttons. Rounded-2xl styling
            // lives in components/ui/sonner.jsx so every toast picks it up.
            offset="calc(88px + env(safe-area-inset-bottom))"
            mobileOffset="calc(80px + env(safe-area-inset-bottom))"
          />
        )}
      </div>
      </PlatformSettingsProvider>
    </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
